import {
  useEffect,
  useRef,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { LogOut, Menu, Search, X } from "lucide-react";

import { ActionCountButton } from "@/action-event-center";
import { RaqozLogo } from "@/components/brand/raqoz-logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { loadWorkspace } from "@/lib/api";
import { navigation } from "@/lib/app-navigation";
import type { View } from "@/lib/app-view";
import { brandingFromSettings } from "@/lib/branding";
import { supabase } from "@/lib/supabase";
import type { AppRole, Organisation } from "@/lib/types";
import { titleCase } from "@/lib/utils";

type Workspace = Awaited<ReturnType<typeof loadWorkspace>>;

export function AppShell({
  workspace,
  organisation,
  role,
  visibleNavigation,
  effectiveView,
  mobileOpen,
  setMobileOpen,
  setOrganisationId,
  navigateTo,
  refresh,
  actionPermissions,
  query,
  setQuery,
  setRecordId,
  recordId,
  children,
}: {
  workspace: Workspace;
  organisation: Organisation;
  role: AppRole;
  visibleNavigation: typeof navigation;
  effectiveView: View;
  mobileOpen: boolean;
  setMobileOpen: Dispatch<SetStateAction<boolean>>;
  setOrganisationId: Dispatch<SetStateAction<string>>;
  navigateTo: (nextView: string, recordId?: string) => void;
  refresh: number;
  actionPermissions: {
    approve: boolean;
    procure: boolean;
    receive: boolean;
    finance: boolean;
    manage: boolean;
  };
  query: string;
  setQuery: Dispatch<SetStateAction<string>>;
  setRecordId: Dispatch<SetStateAction<string>>;
  recordId: string;
  children: ReactNode;
}) {
  const drawerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const organisationBranding = brandingFromSettings(
    organisation.name,
    organisation.settings,
  );
  const subscription = workspace.subscriptions.find(
    (item) => item.organisation_id === organisation.id,
  );
  if (organisationBranding.logoPath)
    organisationBranding.logoUrl = supabase.storage
      .from("raqoz-brand-assets")
      .getPublicUrl(organisationBranding.logoPath).data.publicUrl;

  useEffect(() => {
    if (!mobileOpen) return;
    const drawer = drawerRef.current;
    const menuButton = menuButtonRef.current;
    const previousOverflow = document.body.style.overflow;
    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = () =>
      Array.from(
        drawer?.querySelectorAll<HTMLElement>(focusableSelector) || [],
      ).filter((element) => element.offsetParent !== null);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setMobileOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const elements = focusable();
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    window.requestAnimationFrame(() => focusable()[0]?.focus());
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      window.requestAnimationFrame(() => menuButton?.focus());
    };
  }, [mobileOpen, setMobileOpen]);

  return (
    <div className="min-h-screen bg-[var(--canvas)] text-[var(--text-primary)]">
      <aside
        ref={drawerRef}
        id="workspace-navigation-drawer"
        role={mobileOpen ? "dialog" : undefined}
        aria-modal={mobileOpen ? true : undefined}
        aria-label="Workspace navigation"
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-[var(--nav-surface)] p-4 text-[var(--nav-text)] shadow-[var(--shadow-lg)] transition-[transform,visibility] duration-200 lg:visible lg:translate-x-0 ${mobileOpen ? "visible translate-x-0" : "invisible -translate-x-full"}`}
      >
        <div className="flex items-start justify-between gap-3 px-2 py-3">
          <div className="min-w-0">
            {organisationBranding.logoUrl ? (
              <div>
                <img
                  alt={`${organisation.name} logo`}
                  className="h-10 max-w-[170px] object-contain object-left"
                  src={organisationBranding.logoUrl}
                />
                <span className="mt-2 block text-[9px] font-bold tracking-[.13em] text-white/40">
                  POWERED BY RAQOZ
                </span>
              </div>
            ) : (
              <RaqozLogo inverse />
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="size-11 shrink-0 text-[var(--nav-text)] hover:bg-[var(--nav-hover)] hover:text-[var(--nav-text)] lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          >
            <X className="size-5" aria-hidden="true" />
          </Button>
        </div>
        <nav
          aria-label="Workspace navigation"
          className="mt-4 grid gap-1 overflow-y-auto"
        >
          {visibleNavigation.map((item) => (
            <div key={item.view}>
              {item.group && (
                <p className="mb-2 mt-5 px-3 text-[10px] font-semibold tracking-[.1em] text-[var(--nav-muted)]">
                  {item.group}
                </p>
              )}
              <button
                aria-current={effectiveView === item.view ? "page" : undefined}
                onClick={() => navigateTo(item.view)}
                className={`flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm ${effectiveView === item.view ? "bg-[var(--nav-active)] font-semibold text-white shadow-[inset_3px_0_0_var(--raqoz-mint)]" : "text-[var(--nav-muted)] hover:bg-[var(--nav-hover)] hover:text-[var(--nav-text)]"}`}
              >
                <item.icon className="size-4" aria-hidden="true" />
                {item.label}
              </button>
            </div>
          ))}
        </nav>
        <div className="mt-auto flex items-center gap-3 border-t border-white/10 px-2 pt-4 text-xs">
          <span className="size-2 rounded-full bg-[var(--raqoz-mint)]" />
          <div>
            <strong className="block">{subscription?.commercial_plans?.name || "RAQOZ"} workspace</strong>
            <span className="text-[var(--nav-muted)]">{titleCase(role)} · Requester/approver seats unmetered</span>
          </div>
        </div>
      </aside>
      {mobileOpen && (
        <button
          className="fixed inset-0 z-30 bg-[var(--overlay)] lg:hidden"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex min-h-[68px] items-center justify-between gap-3 border-b border-[var(--border)] bg-white/95 px-4 backdrop-blur md:px-7">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              ref={menuButtonRef}
              variant="ghost"
              size="icon"
              className="size-11 lg:hidden"
              onClick={() => setMobileOpen((value) => !value)}
              aria-label="Open navigation"
              aria-expanded={mobileOpen}
              aria-controls="workspace-navigation-drawer"
            >
              <Menu className="size-5" aria-hidden="true" />
            </Button>
            <Label className="flex min-w-0 grid-cols-[auto_1fr] items-center gap-2">
              <span className="hidden text-[var(--text-secondary)] sm:inline">
                Workspace
              </span>
              <Select
                value={organisation.id}
                onChange={(event) => setOrganisationId(event.target.value)}
                className="w-[42vw] max-w-52 border-0 font-semibold sm:w-auto"
              >
                {workspace.organisations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <ActionCountButton
              organisationId={organisation.id}
              currentUserId={workspace.user.id}
              refresh={refresh}
              permissions={actionPermissions}
              onOpen={() => navigateTo("actions")}
            />
            <div className="relative hidden xl:block">
              <Search className="absolute left-3 top-2.5 size-4 text-[var(--text-muted)]" />
              <Input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setRecordId("");
                }}
                className="w-56 bg-[var(--surface-secondary)] pl-9"
                placeholder="Filter this view"
                aria-label="Filter current view"
              />
            </div>
            <div className="hidden text-right text-xs md:block">
              <strong className="block">{workspace.user.name}</strong>
              <span className="text-[var(--text-secondary)]">
                {titleCase(role)}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="size-11 px-0 sm:h-11 sm:w-auto sm:px-4"
              onClick={() => supabase.auth.signOut()}
            >
              <LogOut className="size-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </header>
        <main className="mx-auto max-w-[1500px] p-4 md:p-7">
          {recordId && (
            <nav
              aria-label="Breadcrumb"
              className="mb-4 flex flex-wrap items-center gap-2 text-xs text-[var(--text-secondary)]"
            >
              <button
                className="hover:text-[var(--action)]"
                onClick={() => navigateTo("dashboard")}
              >
                Workspace
              </button>
              <span aria-hidden="true">/</span>
              <button
                className="hover:text-[var(--action)]"
                onClick={() => navigateTo(effectiveView)}
              >
                {visibleNavigation.find((item) => item.view === effectiveView)?.label ||
                  "Record"}
              </button>
              <span aria-hidden="true">/</span>
              <strong className="font-mono text-[var(--text-primary)]">
                {recordId.slice(0, 8)}
              </strong>
            </nav>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
